/**
 * ReliquidarNetoDialog — recalcular el reparto de una reserva con otro neto.
 *
 * Entretur subió el neto de 220.000 a 240.000 por pasajero, pero hay ventas
 * pactadas con la tarifa anterior. Lo que el cliente paga NO cambia: cambia
 * cómo se reparte entre el neto de Entretur y la comisión de la agencia.
 *
 *     PVP  =  neto de Entretur  +  comisión de la agencia
 *
 * El diálogo muestra el antes y el después antes de confirmar, porque el
 * número que se teclea es el neto por pasajero y lo que cambia es otra cosa
 * —la comisión y lo que queda por cobrar—.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, TextField, Alert, Stack, Divider, FormControlLabel, Checkbox,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import { API_BASE, getAuthHeaders, formatPrecio } from './shared/format';

export default function ReliquidarNetoDialog({
  open, onClose, reserva, resumenFinanciero, onHecho, onToast,
}) {
  const pvp = Number(reserva?.precio_total || 0);
  const cortA = Number(reserva?.cortesias_adultos || 0) || Number(reserva?.cortesias || 0);
  const cortN = Number(reserva?.cortesias_ninos || 0);
  const pagA = Math.max(Number(reserva?.adultos || 0) - cortA, 0);
  const pagN = Math.max(Number(reserva?.ninos || 0) - cortN, 0);

  const [netoAdulto, setNetoAdulto] = useState('220000');
  const [tarifaNino, setTarifaNino] = useState(false);
  const [netoNino, setNetoNino] = useState('210000');
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setNetoAdulto('220000'); setTarifaNino(false); setNetoNino('210000');
    setMotivo(''); setError(null);
  }, [open]);

  const nA = Number(String(netoAdulto).replace(/[^\d]/g, '')) || 0;
  const nN = tarifaNino ? (Number(String(netoNino).replace(/[^\d]/g, '')) || 0) : nA;
  const netoTotal = pagA * nA + pagN * nN;
  const comision = pvp - netoTotal;

  const comisionActual = Number(resumenFinanciero?.comision_efectiva
    ?? reserva?.comision_valor ?? 0);
  const saldoActual = Number(reserva?.saldo_pendiente || 0);
  // Lo que ya entró no se devuelve: el saldo nuevo es el neto menos eso.
  const yaPagado = Math.max(comisionActual + saldoActual === 0 ? 0
    : (pvp - comisionActual) - saldoActual, 0);
  const saldoNuevo = Math.max(netoTotal - yaPagado, 0);

  const excede = comision < 0;
  const puedeGuardar = nA > 0 && !excede && motivo.trim().length >= 4 && !guardando;

  const guardar = async () => {
    setGuardando(true); setError(null);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/correcciones/reservas/${reserva.id}/reliquidar-neto`, {
          method: 'POST', headers: getAuthHeaders(),
          body: JSON.stringify({
            neto_adulto: nA,
            neto_nino: tarifaNino ? nN : null,
            motivo: motivo.trim(),
          }),
        });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail?.message || d.detail || 'No se pudo reliquidar');
      onToast?.success?.(
        `Reliquidada: neto ${formatPrecio(d.neto_total, reserva.moneda)}, `
        + `comisión ${formatPrecio(d.comision, reserva.moneda)}.`);
      onHecho?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'No se pudo reliquidar');
    }
    setGuardando(false);
  };

  const Fila = ({ etiqueta, antes, ahora, color }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.4 }}>
      <Typography sx={{ fontSize: 12, color: '#475569' }}>{etiqueta}</Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
        <Typography sx={{ fontSize: 11.5, color: '#94a3b8', textDecoration: 'line-through' }}>
          {formatPrecio(antes, reserva?.moneda)}
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: color || '#0f172a' }}>
          {formatPrecio(ahora, reserva?.moneda)}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Dialog open={!!open} onClose={guardando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>
        Reliquidar con otro neto
        <Typography sx={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>
          {reserva?.codigo_reserva} · {pagA} adulto(s){pagN ? ` + ${pagN} niño(s)` : ''}
          {(cortA + cortN) ? ` · ${cortA + cortN} de cortesía` : ''}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField label="Neto de Entretur por adulto" size="small" value={netoAdulto}
                     onChange={(e) => setNetoAdulto(e.target.value)}
                     helperText="La tarifa anterior es 220.000; la vigente, 240.000" />

          <Box>
            <FormControlLabel
              control={<Checkbox size="small" checked={tarifaNino}
                                 onChange={(e) => setTarifaNino(e.target.checked)} />}
              label={<Typography sx={{ fontSize: 12.5 }}>
                Los niños tienen otro neto
              </Typography>}
              sx={{ mb: tarifaNino ? 1 : 0 }}
              disabled={pagN === 0}
            />
            {tarifaNino && (
              <TextField label="Neto por niño" size="small" fullWidth value={netoNino}
                         onChange={(e) => setNetoNino(e.target.value)} />
            )}
            {pagN === 0 && (
              <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                Esta reserva no tiene niños pagantes.
              </Typography>
            )}
          </Box>

          {/* El antes y el después, que es lo que de verdad se está decidiendo */}
          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#475569',
                              textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.5 }}>
              Cómo queda el reparto
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
              <Typography sx={{ fontSize: 12, color: '#475569' }}>El cliente paga</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                {formatPrecio(pvp, reserva?.moneda)}
              </Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <Fila etiqueta="Neto de Entretur" antes={pvp - comisionActual} ahora={netoTotal} color="#0d9488" />
            <Fila etiqueta="Comisión de la agencia" antes={comisionActual} ahora={comision} color="#7c3aed" />
            <Fila etiqueta="Por cobrar" antes={saldoActual} ahora={saldoNuevo} color="#b45309" />
            {excede && (
              <Typography sx={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 700, mt: 0.75 }}>
                Con ese neto la comisión queda en negativo. Revisa la cifra.
              </Typography>
            )}
            {yaPagado > 0 && !excede && (
              <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.75 }}>
                Ya entraron {formatPrecio(yaPagado, reserva?.moneda)}: eso no se devuelve, baja lo que falta.
              </Typography>
            )}
          </Box>

          <TextField label="Por qué se reliquida" size="small" multiline minRows={2}
                     value={motivo} onChange={(e) => setMotivo(e.target.value)}
                     placeholder="La venta se pactó con el neto anterior de 220.000"
                     helperText="Queda en la traza de auditoría" />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button variant="contained" startIcon={<CalculateIcon />}
                disabled={!puedeGuardar} onClick={guardar}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
          {guardando ? 'Reliquidando…' : 'Reliquidar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
